import { useEffect, useState } from "react";
import styled from "styled-components";
import { popIn, slideReveal } from "./Autopilot.styled";
import { useBrandKit } from "hooks/useBrandKit/";
import { Skeleton } from "@libertify/ui";
import { useTranslate } from "#src/hooks";
import { FontPreviewText } from "pages/media/brandkit/components/FontPreview";

export const PaletteVisual: React.FC<{
  isActive: boolean;
  isDone: boolean;
  brandKitData: ReturnType<typeof useBrandKit>;
}> = ({ isActive, isDone, brandKitData }) => {
  const t = useTranslate();

  const { brandKitPalette } = brandKitData;

  const colors = brandKitPalette
    ? [
        brandKitPalette.primary_color,
        brandKitPalette.secondary_color,
        brandKitPalette.title_color,
        brandKitPalette.subtitle_color,
        brandKitPalette.title_stroke_color,
        brandKitPalette.subtitle_stroke_color,
      ].filter(Boolean)
    : [];

  const [colorCount, setColorCount] = useState(isDone ? colors.length : 0);
  const [showFonts, setShowFonts] = useState(isDone);

  useEffect(() => {
    if (!brandKitPalette || isDone || !isActive) return;
    const total = colors.length;
    const timers: ReturnType<typeof setTimeout>[] = [];
    timers.push(
      setTimeout(() => {
        setColorCount(0);
        setShowFonts(false);
      }, 0)
    );
    for (let i = 0; i < total; i++) {
      timers.push(setTimeout(() => setColorCount((c) => c + 1), i * 220));
    }
    timers.push(setTimeout(() => setShowFonts(true), total * 220 + 350));
    return () => timers.forEach(clearTimeout);
  }, [isActive, isDone, brandKitPalette]); // eslint-disable-line react-hooks/exhaustive-deps

  // Done steps show everything immediately without animation
  const visibleColorCount = isDone ? colors.length : colorCount;
  const visibleFonts = isDone || showFonts;

  if (!brandKitPalette) {
    return <Skeleton />;
  }

  return (
    <PaletteWrap>
      <ColorRow>
        {colors.map((color, i) => {
          if (i >= visibleColorCount) return null;
          return (
            <ColorItem key={i}>
              <ColorRect $color={color} $delay={0} />
              <ColorHex>{color}</ColorHex>
            </ColorItem>
          );
        })}
      </ColorRow>

      {visibleFonts && (
        <FontSection>
          <FontGroup>
            {brandKitData.titleFont && (
              <FontPreviewText
                font={brandKitData.titleFont.path}
                text={t(
                  "media.brandkit.components.BrandKitSummary.headerPreview"
                )}
                style={{
                  fontSize: 24,
                  fontWeight: 700,
                  color: "#111827",
                  lineHeight: 1.3,
                  marginBottom: 3,
                  textAlign: "left",
                  padding: 0,
                }}
              />
            )}
            {brandKitData.subtitlesFont && (
              <FontPreviewText
                font={brandKitData.subtitlesFont.path}
                text={t(
                  "media.brandkit.components.BrandKitSummary.subtitlesAutopilot"
                )}
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: "#111827",
                  lineHeight: 1.3,
                  marginBottom: 3,
                  textAlign: "left",
                  padding: 0,
                }}
              />
            )}
            {brandKitData.textFont && (
              <FontPreviewText
                font={brandKitData.textFont.path}
                text={t(
                  "media.brandkit.components.BrandKitSummary.paragraphAutopilot"
                )}
                style={{
                  fontSize: 13,
                  fontWeight: 400,
                  color: "#111827",
                  lineHeight: 1.3,
                  marginBottom: 3,
                  textAlign: "left",
                  padding: 0,
                }}
              />
            )}
          </FontGroup>
        </FontSection>
      )}
    </PaletteWrap>
  );
};

const PaletteWrap = styled.div`
  padding: 14px 15px 12px;
  animation: ${slideReveal} 0.3s ease both;
`;

const ColorRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 14px;
  flex-wrap: wrap;
`;

const ColorRect = styled.div<{ $color: string; $delay: number }>`
  width: 80px;
  height: 48px;
  border-radius: 10px;
  border: solid 1px #eef1f8;
  background: ${(p) => p.$color};
  flex-shrink: 0;
  animation: ${popIn} 0.3s ease ${(p) => p.$delay}ms both;
`;

const ColorItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

const ColorHex = styled.span`
  font-size: 10px;
  font-weight: 700;
  font-family: monospace;
  letter-spacing: 0.4px;
  color: #9ca3af;
`;

const FontSection = styled.div`
  animation: ${slideReveal} 0.3s ease 0.35s both;
`;

const FontGroup = styled.div`
  margin-bottom: 14px;
`;
